import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, ArrowLeft, IndianRupee, Building2, Copy, Upload, QrCode,
  Sparkles, Users, Lightbulb, MessageSquare,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import SearchableSelect from '../../components/ui/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const bankDetails = {
  accountName: 'Brahmkshatriya Marwari Khatri Association',
  bankName: 'Bank of India',
  accountNumber: '661020110000985',
  ifscCode: 'BKID0006610',
  branch: 'Chopasni Road, Jodhpur',
};

const VisionConventionRegistration = () => {
  const [options, setOptions] = useState({ nukhs: [], states: [], paymentModes: [], topics: [], registrationFee: 500 });
  const [form, setForm] = useState({
    name: '',
    gender: '',
    fatherHusbandName: '',
    nukh: '',
    age: '',
    place: '',
    address: '',
    state: '',
    mobile: '',
    email: '',
    wantsPresent: 'no',
    topic: '',
    paymentMode: '',
    utr: '',
    paymentDate: '',
    remarks: '',
    declaration: false,
  });
  const [photo, setPhoto] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { registrationCode }

  useEffect(() => {
    axios.get(`${API}/vc/options`).then(r => setOptions(r.data)).catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const validate = () => {
    if (!form.name.trim()) { toast.error('Please enter participant name'); return false; }
    if (!form.gender) { toast.error('Please select gender'); return false; }
    if (!form.fatherHusbandName.trim()) { toast.error("Please enter father's / husband's name"); return false; }
    if (!form.nukh) { toast.error('Please select nukh'); return false; }
    if (!form.age || Number(form.age) < 5 || Number(form.age) > 120) { toast.error('Please enter a valid age'); return false; }
    if (!form.place.trim()) { toast.error('Please enter place (city / village)'); return false; }
    if (!form.address.trim()) { toast.error('Please enter full address'); return false; }
    if (!form.state) { toast.error('Please select state'); return false; }
    if (!form.mobile || form.mobile.length !== 10 || !/^\d+$/.test(form.mobile)) { toast.error('Mobile must be 10 digits'); return false; }
    if (!form.email.trim()) { toast.error('Please enter email'); return false; }
    if (form.wantsPresent === 'yes' && !form.topic) { toast.error('Please pick a topic'); return false; }
    if (!form.paymentMode) { toast.error('Please select payment mode'); return false; }
    if (!form.utr.trim()) { toast.error('Please enter UTR / transaction number'); return false; }
    if (!form.paymentDate) { toast.error('Please pick payment date'); return false; }
    if (!photo) { toast.error('Please upload your passport-size photo'); return false; }
    if (!receipt) { toast.error('Please upload payment receipt / screenshot'); return false; }
    if (!form.declaration) { toast.error('Please accept the declaration'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'wantsPresent') {
          fd.append(k, v === 'yes' ? 'true' : 'false');
        } else if (k === 'declaration') {
          fd.append(k, v ? 'true' : 'false');
        } else {
          fd.append(k, v);
        }
      });
      fd.append('photo', photo);
      fd.append('receipt', receipt);

      const res = await axios.post(`${API}/vc/register`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      toast.success('Registration submitted successfully');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 py-12 px-4">
        <div className="max-w-xl mx-auto text-center">
          <div className="mx-auto mb-6 w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3">पंजीकरण सफलतापूर्वक जमा हुआ</h1>
          <p className="text-gray-600 mb-6">आपका पंजीकरण संख्या:</p>
          <div className="text-4xl font-mono font-bold text-orange-600 tracking-wider mb-8" data-testid="vc-reg-code">
            {result.registrationCode}
          </div>
          <p className="text-sm text-gray-500 mb-8">
            कृपया इस कोड को सुरक्षित रखें। स्वीकृति के बाद आपको ईमेल पर सूचित किया जाएगा।
          </p>
          <Link to="/">
            <Button variant="outline" data-testid="vc-back-home">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center text-sm text-gray-600 hover:text-orange-600 mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Home
        </Link>

        {/* Header */}
        <Card className="mb-6 border-orange-100 bg-white/90 backdrop-blur overflow-hidden">
          {/* Sanskrit invocation strip */}
          <div className="bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 text-white text-center py-2 text-sm font-semibold tracking-wide">
            श्री हिंगुलायै नमः
          </div>

          <div className="p-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
              <img
                src="/vc-logo-seal.png"
                alt="Vision Convention 2026"
                className="w-24 h-24 sm:w-28 sm:h-28 object-contain flex-shrink-0 drop-shadow-md"
                data-testid="vc-form-logo"
              />
              <div className="flex-1 text-center sm:text-left">
                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold mb-1">
                  <Sparkles className="w-3 h-3" /> Vision Sangam 2026
                </div>
                <p className="text-sm font-medium text-gray-700">
                  ब्रह्मक्षत्रिय मारवाड़ी खत्री एसोसिएशन – महासभा
                </p>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight mt-1">
                  समाज चिंतन एवं विज़न संगम – 2026
                </h1>
                <p className="mt-1 text-sm italic text-orange-700 font-medium" data-testid="vc-tagline">
                  संगम विचारों का, संकल्प भविष्य का
                </p>
              </div>
            </div>

            <p className="mt-5 text-sm text-gray-700 leading-relaxed">
              समाज के वर्तमान एवं भविष्य से जुड़े महत्वपूर्ण विषयों पर व्यापक चिंतन एवं दूरदर्शी कार्ययोजना
              (Vision Program) तैयार करने के उद्देश्य से महासभा द्वारा <strong>सितंबर 2026 में जोधपुर</strong> में
              &quot;समाज चिंतन एवं विजन संगम&quot; आयोजित किया जा रहा है। विजन संगम का मंच अभिव्यक्ति के लिये खुला रहेगा
              लेकिन जो अभिव्यक्ति देना चाहते हैं, वे फॉर्म में &quot;विचार अभिव्यक्ति&quot; कॉलम में &quot;हां&quot; चयनित करें व
              अपने विषय (किसी एक) पर टिक अवश्य करें।
            </p>

            <p className="mt-4 text-sm font-semibold text-gray-800">
              सम्मेलन में निम्न प्रमुख विषयों पर विचार-विमर्श किया जाएगा—
            </p>
            <ul className="mt-2 space-y-1.5 text-sm text-gray-700">
              {[
                'सगाई एवं वैवाहिक संबंधों में आ रही कठिनाइयाँ',
                'विवाह पश्चात उत्पन्न होने वाली चुनौतियाँ',
                'समाज के रीति-रिवाज व परम्परायें',
                'शिक्षा एवं मेडिकल सहायता कोष की आवश्यकता',
                'युवा एवं महिला नेतृत्व को प्रोत्साहन तथा उनकी सक्रिय भागीदारी',
                'समाज को ओबीसी में स्थान दिलाने के प्रयास',
                'राजनीति एवं प्रशासनिक सेवाओं में समाजबंधुओं की सक्रिय सहभागिता',
                'समाज के संगठन को भविष्य की आवश्यकताओं के अनुरूप सशक्त एवं प्रभावी बनाना',
                'महासभा की आमसभा एवं संविधान में आवश्यक संशोधनों पर विचार',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-5 rounded-lg bg-orange-50 border border-orange-100 p-4 text-sm text-gray-700 leading-relaxed">
              <strong>18 वर्ष या उससे अधिक आयु</strong> के सभी समाजबंधु एवं समाजबहनों को सादर आमंत्रित किया जाता है।
            </div>

            <p className="mt-4 text-sm text-gray-700 leading-relaxed">
              समाज के उज्ज्वल भविष्य के निर्माण में आपके विचार, सुझाव एवं सहभागिता अत्यंत महत्वपूर्ण हैं।
              अतः कृपया <strong>₹{options.registrationFee} का सहयोग-योगदान</strong> देकर यहां दिए गए पंजीकरण फॉर्म को
              आज ही भरें तथा इस <strong>ऐतिहासिक चिंतन और परिवर्तन के सहभागी</strong> बनें।
            </p>

            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold" data-testid="vc-form-last-date">
              पंजीकरण की अंतिम तारीख: <span className="line-through opacity-70">31 अगस्त 2026</span>&nbsp;<span className="font-bold">बढ़ाकर 30 सितंबर 2026</span>
            </div>
          </div>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Participant details */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-500" /> प्रतिभागी विवरण / Participant Details
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>प्रतिभागी का नाम <span className="text-red-500">*</span></Label>
                <Input name="name" value={form.name} onChange={handleChange} data-testid="vc-name" />
              </div>
              <div>
                <Label>झेंडर / Gender <span className="text-red-500">*</span></Label>
                <select name="gender" value={form.gender} onChange={handleChange} className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm" data-testid="vc-gender">
                  <option value="">Select</option>
                  <option value="पुरुष">पुरुष / Male</option>
                  <option value="महिला">महिला / Female</option>
                </select>
              </div>
              <div>
                <Label>पिता / पति का नाम <span className="text-red-500">*</span></Label>
                <Input name="fatherHusbandName" value={form.fatherHusbandName} onChange={handleChange} data-testid="vc-father-husband" />
              </div>
              <div>
                <Label>नुख / Nukh <span className="text-red-500">*</span></Label>
                <SearchableSelect
                  options={options.nukhs || []}
                  value={form.nukh}
                  onChange={(value) => setForm(prev => ({ ...prev, nukh: value }))}
                  placeholder="Type to search nukh..."
                  filterMode="startsWith"
                  allowCustom={false}
                  name="nukh"
                  required
                />
              </div>
              <div>
                <Label>आयु / Age <span className="text-red-500">*</span></Label>
                <Input type="number" name="age" value={form.age} onChange={handleChange} min="5" max="120" data-testid="vc-age" />
              </div>
              <div>
                <Label>स्थान (शहर / गाँव) / Place <span className="text-red-500">*</span></Label>
                <Input name="place" value={form.place} onChange={handleChange} data-testid="vc-place" />
              </div>
              <div>
                <Label>राज्य / State <span className="text-red-500">*</span></Label>
                <select name="state" value={form.state} onChange={handleChange} className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm" data-testid="vc-state">
                  <option value="">Select State</option>
                  {options.states.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label>मोबाइल / Mobile <span className="text-red-500">*</span></Label>
                <Input name="mobile" value={form.mobile} onChange={handleChange} maxLength={10} placeholder="10 digits" data-testid="vc-mobile" />
              </div>
              <div className="md:col-span-2">
                <Label>पूरा पता / Full Address <span className="text-red-500">*</span></Label>
                <Textarea name="address" value={form.address} onChange={handleChange} rows={2} data-testid="vc-address" />
              </div>
              <div className="md:col-span-2">
                <Label>ईमेल / Email <span className="text-red-500">*</span></Label>
                <Input type="email" name="email" value={form.email} onChange={handleChange} data-testid="vc-email" />
              </div>

              <div className="md:col-span-2">
                <Label>प्रतिभागी का फोटो अपलोड करें (पासपोर्ट साइज) <span className="text-red-500">*</span></Label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                    className="text-sm"
                    data-testid="vc-photo"
                  />
                  {photo && <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {photo.name}</span>}
                </div>
              </div>
            </div>
          </Card>

          {/* Idea presentation */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-orange-500" /> विचार प्रस्तुति / Idea Presentation
            </h2>
            <div>
              <Label>क्या आप सम्मेलन में अपने विचार प्रस्तुत करना चाहते हैं ? <span className="text-red-500">*</span></Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2">
                  <input type="radio" name="wantsPresent" value="yes" checked={form.wantsPresent === 'yes'} onChange={handleChange} data-testid="vc-wants-yes" />
                  <span>हाँ / Yes</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="wantsPresent" value="no" checked={form.wantsPresent === 'no'} onChange={handleChange} data-testid="vc-wants-no" />
                  <span>नहीं / No</span>
                </label>
              </div>
            </div>
            {form.wantsPresent === 'yes' && (
              <div className="mt-4">
                <Label>आप किस विषय पर अपने विचार प्रस्तुत करना चाहते हैं ? <span className="text-red-500">*</span></Label>
                <div className="space-y-2 mt-2">
                  {options.topics.map(t => (
                    <label key={t} className={`flex items-start gap-2 p-3 rounded-lg border-2 cursor-pointer transition ${form.topic === t ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <input type="radio" name="topic" value={t} checked={form.topic === t} onChange={handleChange} className="mt-1" data-testid={`vc-topic-${t.slice(0, 6)}`} />
                      <span className="text-sm">{t}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Payment */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-orange-500" /> पंजीकरण शुल्क ₹{options.registrationFee}
            </h2>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-orange-50 border border-orange-100 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-gray-800">
                  <Building2 className="w-4 h-4 text-orange-500" /> Bank Details
                </div>
                <div className="space-y-1.5 text-sm">
                  <div><span className="text-gray-500">Account Name: </span>{bankDetails.accountName}</div>
                  <div><span className="text-gray-500">Bank: </span>{bankDetails.bankName}</div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">A/c No: </span>{bankDetails.accountNumber}
                    <button type="button" onClick={() => copyToClipboard(bankDetails.accountNumber, 'Account number')} className="text-orange-500 hover:text-orange-700">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">IFSC: </span>{bankDetails.ifscCode}
                    <button type="button" onClick={() => copyToClipboard(bankDetails.ifscCode, 'IFSC')} className="text-orange-500 hover:text-orange-700">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div><span className="text-gray-500">Branch: </span>{bankDetails.branch}</div>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="text-xs text-gray-500 flex items-center gap-1"><QrCode className="w-3.5 h-3.5" /> Scan with any UPI app</div>
                <img
                  src="https://customer-assets.emergentagent.com/job_abcfafc4-09e8-4929-917e-c56a46d51339/artifacts/bbdulxwl_Mahasabha%20QR%20code.jpeg"
                  alt="UPI QR"
                  className="w-40 h-auto rounded-lg border border-gray-200 shadow-sm bg-white p-2"
                  data-testid="vc-upi-qr"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>भुगतान का माध्यम <span className="text-red-500">*</span></Label>
                <select name="paymentMode" value={form.paymentMode} onChange={handleChange} className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm" data-testid="vc-payment-mode">
                  <option value="">Select</option>
                  {options.paymentModes.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <Label>UTR / Transaction # <span className="text-red-500">*</span></Label>
                <Input name="utr" value={form.utr} onChange={handleChange} data-testid="vc-utr" />
              </div>
              <div>
                <Label>भुगतान की तिथि <span className="text-red-500">*</span></Label>
                <Input type="date" name="paymentDate" value={form.paymentDate} onChange={handleChange} data-testid="vc-payment-date" />
              </div>
            </div>

            <div className="mt-4">
              <Label>भुगतान की रसीद / स्क्रीनशॉट अपलोड करें <span className="text-red-500">*</span></Label>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setReceipt(e.target.files?.[0] || null)}
                  className="text-sm"
                  data-testid="vc-receipt"
                />
                {receipt && <span className="text-xs text-emerald-600 flex items-center gap-1"><Upload className="w-3 h-3" /> {receipt.name}</span>}
              </div>
            </div>
          </Card>

          {/* Remarks */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-orange-500" />
              प्रतिभागी की टिप्पणी / Remarks by Participant
              <span className="text-xs font-normal text-gray-500">(Optional)</span>
            </h2>
            <Label className="text-xs text-gray-500 leading-relaxed">
              कोई विशेष सूचना, अनुरोध या टिप्पणी लिखें (जैसे — विचार अभिव्यक्ति चयनित है, पर मंच पर नहीं बोलना चाहते; सुझाव लिखित रूप में जमा करेंगे)।
            </Label>
            <Textarea
              name="remarks"
              value={form.remarks}
              onChange={handleChange}
              rows={3}
              maxLength={1000}
              placeholder="कृपया कोई अतिरिक्त जानकारी, विशेष अनुरोध या टिप्पणी यहाँ लिखें… / Add any additional information, special requests, or notes here."
              className="mt-2 resize-y"
              data-testid="vc-remarks"
            />
            <div className="mt-1 text-right text-xs text-gray-400">
              {form.remarks.length}/1000
            </div>
          </Card>

          {/* Declaration */}
          <Card className="p-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="declaration"
                checked={form.declaration}
                onChange={handleChange}
                className="mt-1"
                data-testid="vc-declaration"
              />
              <span className="text-sm text-gray-700 leading-relaxed">
                <span className="block font-semibold text-gray-900 mb-1">घोषणा</span>
                मैं घोषणा करता हूं / करती हूं कि मेरे द्वारा दी गई समस्त जानकारी सत्य एवं सही है।
                मैं <strong>विजन संगम 2026</strong> की समस्त नियमावली, प्रक्रिया, मर्यादा एवं अनुशासन का
                पूर्णतः पालन करूंगा / करूंगी।
                <span className="block font-semibold text-gray-900 mt-3 mb-1">Declaration</span>
                <span className="text-gray-500">
                  I hereby declare that all the information provided by me is true and correct.
                  I agree to abide by all the rules, procedures, code of conduct, and discipline of Vision Sangam 2026.
                </span>
              </span>
            </label>
          </Card>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 text-base"
            data-testid="vc-submit"
          >
            {loading ? 'Submitting…' : 'पंजीकरण सबमिट करें / Submit Registration'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default VisionConventionRegistration;
